import type { ReactElement, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Upload,
  FileJson,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  Building2,
  Plus,
  ShieldCheck,
} from 'lucide-react'
import type { ImportResponse, ValidateXeroOrgsResponse } from '../../types'

const BYTES_PER_KIB = 1024

interface DropZoneProps {
  selectedFile: File | null
  isDragging: boolean
  fileInputRef: RefObject<HTMLInputElement | null>
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function XeroDropZone({
  selectedFile,
  isDragging,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
}: DropZoneProps): ReactElement {
  const { t } = useTranslation('admin')
  const className = `relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
    isDragging
      ? 'border-[#8a9bba] bg-[#4e5e82]/5 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10'
      : 'border-stone-300 hover:border-stone-400 dark:border-stone-600 dark:hover:border-stone-500'
  }`
  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} className={className}>
      <input
        ref={fileInputRef as RefObject<HTMLInputElement>}
        type="file"
        accept=".json,application/json"
        onChange={onFileSelect}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
      {selectedFile ? (
        <div className="flex flex-col items-center">
          <FileJson className="h-12 w-12 text-[#5c6e94] dark:text-[#8a9bba]" />
          <p className="mt-2 font-medium text-stone-900 dark:text-white">{selectedFile.name}</p>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {(selectedFile.size / BYTES_PER_KIB).toFixed(1)} KB
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <Upload className="h-12 w-12 text-stone-400 dark:text-stone-500" />
          <p className="mt-2 font-medium text-stone-700 dark:text-stone-300">
            {t('licensing.dragDropJson')}
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t('licensing.orClickToBrowse')}
          </p>
        </div>
      )}
    </div>
  )
}

interface ValidateButtonProps {
  selectedFile: File | null
  isValidating: boolean
  isUploading: boolean
  onValidate: () => void
}

function ValidateButton({
  selectedFile,
  isValidating,
  isUploading,
  onValidate,
}: ValidateButtonProps): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onValidate}
      disabled={!selectedFile || isValidating || isUploading}
      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isValidating ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('licensing.xero.validating')}
        </>
      ) : (
        <>
          <ShieldCheck className="h-4 w-4" />
          {t('licensing.xero.validateOrganisations')}
        </>
      )}
    </button>
  )
}

interface UploadButtonProps {
  selectedFile: File | null
  isUploading: boolean
  validationResult: ValidateXeroOrgsResponse | null
  onUpload: () => void
}

function UploadButton({
  selectedFile,
  isUploading,
  validationResult,
  onUpload,
}: UploadButtonProps): ReactElement {
  const { t } = useTranslation('admin')
  const canUpload = validationResult?.isValid ?? false
  return (
    <button
      onClick={onUpload}
      disabled={!selectedFile || isUploading || !canUpload}
      className="flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      title={canUpload ? undefined : t('licensing.xero.validateOrgsFirst')}
    >
      {isUploading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('licensing.importing')}
        </>
      ) : (
        <>
          <Upload className="h-4 w-4" />
          {t('licensing.import')}
        </>
      )}
    </button>
  )
}

interface ClearButtonProps {
  isUploading: boolean
  isValidating: boolean
  onClear: () => void
}

function ClearButton({ isUploading, isValidating, onClear }: ClearButtonProps): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onClear}
      disabled={isUploading || isValidating}
      className="flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
    >
      <X className="h-4 w-4" />
      {t('licensing.clear')}
    </button>
  )
}

interface ActionButtonsProps {
  selectedFile: File | null
  isValidating: boolean
  isUploading: boolean
  validationResult: ValidateXeroOrgsResponse | null
  onValidate: () => void
  onUpload: () => void
  onClear: () => void
}

export function XeroActionButtons({
  selectedFile,
  isValidating,
  isUploading,
  validationResult,
  onValidate,
  onUpload,
  onClear,
}: ActionButtonsProps): ReactElement {
  return (
    <div className="mt-4 flex gap-3">
      <ValidateButton
        selectedFile={selectedFile}
        isValidating={isValidating}
        isUploading={isUploading}
        onValidate={onValidate}
      />
      <UploadButton
        selectedFile={selectedFile}
        isUploading={isUploading}
        validationResult={validationResult}
        onUpload={onUpload}
      />
      {selectedFile && (
        <ClearButton isUploading={isUploading} isValidating={isValidating} onClear={onClear} />
      )}
    </div>
  )
}

interface MissingOrgRowProps {
  xeroOrgId: string
  onCreateOrganisation?: ((xeroOrganisationId: string) => void) | undefined
}

function MissingOrgRow({ xeroOrgId, onCreateOrganisation }: MissingOrgRowProps): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white p-2 dark:bg-stone-800">
      <div className="flex items-center gap-2 text-sm">
        <Building2 className="h-4 w-4 text-stone-400" />
        <code className="font-mono text-xs text-stone-600 dark:text-stone-400">{xeroOrgId}</code>
      </div>
      {onCreateOrganisation && (
        <button
          type="button"
          onClick={() => {
            onCreateOrganisation(xeroOrgId)
          }}
          className="flex items-center gap-1 rounded-md bg-[#4e5e82] px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-[#5c6e94]"
        >
          <Plus className="h-3 w-3" />
          {t('licensing.xero.createOrg')}
        </button>
      )}
    </div>
  )
}

interface ValidationPanelProps {
  validationResult: ValidateXeroOrgsResponse
  onCreateOrganisation?: ((xeroOrganisationId: string) => void) | undefined
}

export function XeroValidationPanel({
  validationResult,
  onCreateOrganisation,
}: ValidationPanelProps): ReactElement {
  const { t } = useTranslation('admin')
  const containerClass = `mt-4 rounded-lg border p-4 ${
    validationResult.isValid
      ? 'border-[#4e5e82]/15 bg-[#4e5e82]/5 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10'
      : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
  }`
  const titleClass = `font-medium ${
    validationResult.isValid
      ? 'text-[#5c6e94] dark:text-[#8a9bba]'
      : 'text-amber-700 dark:text-amber-300'
  }`
  const linkedCount = Object.keys(validationResult.knownOrganisations).length
  return (
    <div className={containerClass}>
      <div className="flex items-start gap-3">
        {validationResult.isValid ? (
          <CheckCircle className="h-5 w-5 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
        ) : (
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        )}
        <div className="flex-1">
          <p className={titleClass}>
            {validationResult.isValid
              ? t('licensing.xero.allOrgsValidated')
              : t('licensing.xero.orgsNotFound', {
                  count: validationResult.missingOrganisations.length,
                })}
          </p>
          {linkedCount > 0 && (
            <div className="mt-2">
              <p className="text-sm text-stone-600 dark:text-stone-400">
                {t('licensing.xero.xeroOrgsLinked', { count: linkedCount })}
              </p>
            </div>
          )}
          {validationResult.missingOrganisations.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                {t('licensing.xero.createMissingOrgs')}
              </p>
              {validationResult.missingOrganisations.map((xeroOrgId) => (
                <MissingOrgRow
                  key={xeroOrgId}
                  xeroOrgId={xeroOrgId}
                  onCreateOrganisation={onCreateOrganisation}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function XeroErrorAlert({ error }: { error: string }): ReactElement {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
    </div>
  )
}

interface ResultStatProps {
  label: string
  value: number
  highlight?: 'success' | 'fail'
}

function ResultStat({ label, value, highlight }: ResultStatProps): ReactElement {
  const valueClass =
    highlight === 'success'
      ? 'font-medium text-[#5c6e94] dark:text-[#8a9bba]'
      : highlight === 'fail' && value > 0
        ? 'font-medium text-red-600 dark:text-red-400'
        : 'font-medium text-stone-700 dark:text-stone-300'
  return (
    <div>
      <span className="text-stone-500 dark:text-stone-400">{label}:</span>{' '}
      <span className={valueClass}>{value}</span>
    </div>
  )
}

export function XeroImportResultPanel({ result }: { result: ImportResponse }): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="mt-4 rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 p-4 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <div className="flex items-start gap-3">
        <CheckCircle className="h-5 w-5 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
        <div className="flex-1">
          <p className="font-medium text-[#5c6e94] dark:text-[#8a9bba]">{result.message}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <ResultStat label={t('licensing.processed')} value={result.result.recordsProcessed} />
            <ResultStat
              label={t('licensing.imported')}
              value={result.result.recordsImported}
              highlight="success"
            />
            <ResultStat label={t('licensing.updated')} value={result.result.recordsUpdated} />
            <ResultStat
              label={t('licensing.failed')}
              value={result.result.recordsFailed}
              highlight="fail"
            />
          </div>
          {result.result.paymentGatesPassed > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-[#5c6e94] dark:text-[#8a9bba]" />
              <span className="text-[#5c6e94] dark:text-[#8a9bba]">
                {t('licensing.xero.paymentGatesPassed', {
                  count: result.result.paymentGatesPassed,
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function XeroFormatHelp(): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <details className="mt-4">
      <summary className="cursor-pointer text-sm font-medium text-stone-600 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-200">
        {t('licensing.viewExpectedJsonFormat')}
      </summary>
      <pre className="mt-2 overflow-x-auto rounded-lg bg-stone-100 p-4 text-xs text-stone-700 dark:bg-stone-900 dark:text-stone-300">
        {`{
  "invoices": [
    {
      "xeroTenantId": "uuid",
      "xeroOrganisationId": "uuid",
      "xeroInvoiceId": "uuid",
      "xeroInvoiceNumber": "INV-001",
      "status": "PAID",
      "amountDue": 0,
      "amountPaid": 1500.00,
      "currencyCode": "NZD",
      "invoiceDate": "2025-01-01",
      "fullyPaidOn": "2025-01-15",
      "entitlementId": "uuid (optional)"
    }
  ]
}`}
      </pre>
    </details>
  )
}
